import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X } from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { ReceiptPreview } from "../../components/ReceiptPreview";
import { AuthImage } from "../../components/AuthImage";
import { useI18n } from "../../i18n";
import { usePlans } from "../../lib/plans";
import { api, formatError, fmtETB } from "../../lib/api";

const FILTERS = ["submitted", "paid", "rejected", "all"];

export const PaymentReviewTab = ({ onChange }) => {
  const { t } = useI18n();
  const { planName, getPlan } = usePlans();
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("submitted");

  const load = useCallback(() => api.get("/admin/users").then((r) => setUsers(r.data.filter((u) => (filter === "all" ? u.receipt_path : u.payment_status === filter)))), [filter]);
  useEffect(() => { load(); }, [load]);

  const act = async (u, approve) => {
    try {
      await api.put(`/admin/users/${u.id}`, approve ? { status: "approved", payment_status: "paid" } : { status: "rejected", payment_status: "rejected" });
      toast.success(t("adm_saved")); load(); onChange();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} className={filter === f ? "btn-gold !px-4 !py-1.5" : "btn-ghost-gold !px-4 !py-1.5"} data-testid={`review-filter-${f}`}>
            {f === "all" ? t("adm_all") : t(`pay_${f}`)}
          </button>
        ))}
      </div>
      {users.length === 0 && <div className="lux-card p-10 text-center text-muted-foreground" data-testid="review-empty">{t("adm_review_empty")}</div>}
      {users.map((u) => (
        <div key={u.id} className="lux-card grid gap-5 p-5 md:grid-cols-[176px_1fr_auto]" data-testid={`review-card-${u.id}`}>
          <ReceiptPreview path={u.receipt_path} className="h-44 w-full md:w-44" testId={`review-receipt-${u.id}`} />
          <div className="flex gap-4">
            <AuthImage path={u.photo_path} className="h-16 w-16 shrink-0 rounded-full border" />
            <div className="min-w-0 space-y-1 text-sm">
              <p className="font-serif text-xl font-semibold">{u.full_name}</p>
              <p className="font-mono text-xs text-muted-foreground">{u.phone}{u.member_id && ` · ${u.member_id}`}</p>
              <p>{planName(u.package)} · <span className="font-semibold" style={{ color: "var(--gold)" }}>{fmtETB(getPlan(u.package)?.fee)} ETB</span></p>
              <p className="text-muted-foreground">{t("pay_method")}: {u.payment_method || "—"}</p>
              {u.payment_submitted_at && <p className="text-xs text-muted-foreground">{t("adm_submitted_at")}: {new Date(u.payment_submitted_at).toLocaleString()}</p>}
              <div className="flex flex-wrap gap-2 pt-1">
                <Badge variant="outline" data-testid={`review-status-${u.id}`}>{t(`st_${u.status}`)}</Badge>
                <Badge variant="outline" data-testid={`review-payment-${u.id}`}>{t(`pay_${u.payment_status}`)}</Badge>
              </div>
            </div>
          </div>
          <div className="flex gap-2 md:w-36 md:flex-col">
            <button onClick={() => act(u, true)} disabled={u.status === "approved" && u.payment_status === "paid"} className="btn-gold flex-1 !py-2 md:flex-none" data-testid={`review-approve-${u.id}`}><Check className="h-4 w-4" />{t("adm_approve")}</button>
            <button onClick={() => act(u, false)} disabled={u.payment_status === "rejected"} className="flex flex-1 items-center justify-center gap-2 rounded-full border border-red-500/60 px-4 py-2 text-sm font-semibold text-red-500 transition-colors hover:bg-red-500/10 disabled:opacity-40 md:flex-none" data-testid={`review-reject-${u.id}`}><X className="h-4 w-4" />{t("adm_reject")}</button>
          </div>
        </div>
      ))}
    </div>
  );
};
